import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  useTable,
  useRowSelect,
  useSortBy,
  useFilters,
  useGlobalFilter,
  usePagination,
  Row,
  SortingRule 
} from "react-table";

// Material UI Core Components
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { useTheme, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MaterialUITable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";

// Custom Components
import TableCheckbox from "@/ui/Table/TableCheckbox";
import TableHeading from "@/ui/Table/TableHeading";
import TableSearch from "@/ui/Table/TableSearch";
import TableHeaderButton from "./HeaderButtons/TableHeaderButton";
import TableHeaderButtonRefresh from "@/ui/Table/HeaderButtons/TableHeaderButtonRefresh";
import TableHeaderButtonColumnPicker from "@/ui/Table/HeaderButtons/TableHeaderButtonColumnPicker";
import TableHeaderButtonExport from "@/ui/Table/HeaderButtons/TableHeaderButtonExport";
import TableHeaderButtonFilter from "@/ui/Table/HeaderButtons/TableHeaderButtonFilter";
import ToolbarFillContent from "@/ui/Toolbar/ToolbarFillContent";
import ThemeContext from "@/components/ThemeContext/ThemeContext";
import UserContext from "@/components/UserContext/UserContext";

import {
  ITableProps,
  IFilterValue,
  RowClickFn,
  GlobalFilterFn,
  RowField
} from "@/@types/ui/Table";
import { DCRecord } from "@/@types/lib/dataController";

interface IFilterState {
  [key: string]: IFilterValue;
}

interface IFilterToggleState {
  [key: string]: boolean
}

interface IColumn {
  source: string;
  hidden: boolean;
}

interface ISort {
  id: string;
  desc?: boolean;
}

function Table(props: ITableProps) {
  // Use the state and functions returned from useTable to build your UI
  const themeContext = useContext(ThemeContext);
  const userContext = useContext(UserContext);

  const [rowId, setRowId] = useState(-1);
  const [recordsNumber, setRecordsNumber] = useState(0);
  const [firstEntry, setFirstEntry] = useState(false);

  const { t } = useTranslation();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const {
    columns,
    data,
    records,
    hiddenColumnNames,
    // onToggleColumnVisibility,
    onRefresh,
    children,
    allowColumnPicker,
    allowSelection,
    allowFilter,
    allowExport,
    allowAdd,
    allowSearch,
    addLabel,
    handleAdd,
    onSelectRecord,
    forceIconButtons,
    // topAlignedSearch,
    dc,
    tableName,
    viewName,
    title,
    // pagination,
    // headerContent,
    identifier,
    onRowClick,
    onDoubleClick,
    fetchData,
    initialPageSize = 10,
    rowSelectedCustom,
    disableSmallSearch,
    exportLabel
  } = props;


  const [showFilters, setShowFilters] = useState<boolean>(userContext ? (userContext.getRecent('toggle_filter', identifier, { show: false }) as IFilterToggleState).show : false);

  useEffect(() => {
    if (rowSelectedCustom) {
      setRowId(rowSelectedCustom);
    }
  }, [rowSelectedCustom]);

  useEffect(() => {
    goToSelectedRowPage();
  }, [rowId]);

  const goToSelectedRowPage = () => {
    // iterate thourgh rows, find on which page row id is (use pageSize)
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const { original } = row as RowField;
      if (original['id'] === rowId) {  
        gotoPage(Math.floor(i / pageSize));
        break;
      }
    }
  }





  // const [filters, setFiltersToggle] = useState<IFilterState | null>(userContext !== null ?
  //   userContext.getRecent("toggle_filter", identifier, {}) as IFilterState
  // : null);

  // const selectOnlyOne = function(evt, value, onChange, toggleAllRowsSelected) {
  //   toggleAllRowsSelected(false);
  //   onChange(evt, value);
  // };

  const handleRowClick: RowClickFn = function (evt, toggleRowSelected, toggleAllRowsSelected, recordId) {
    switch (allowSelection) {
      case "none":
        return;
      case "one":
        if (recordId !== rowId) {
          setRowId(recordId);
          if (onSelectRecord) {
            onSelectRecord(recordId);
          }
          toggleAllRowsSelected(false);
          toggleRowSelected();
          if (onRowClick) {
            onRowClick(recordId);
          }
        }
        return;
      case "many":
        toggleRowSelected();
        break;
    }
  };

  const handleDoubleClick: RowClickFn = function (evt, toggleRowSelected, toggleAllRowsSelected, recordId) {
    if (recordId !== rowId) {
      toggleAllRowsSelected(false);
      if (onDoubleClick) {
        onDoubleClick(recordId);
      }
    }
  };

  const src = dc.getSource();

  // If a new type has a return value that is a div, please define value={value} in its props in order for the column to be searchable
  // If that isn't enough for the new type, define a new case for the new type created here in globalFilter
  const globalFilterFn: GlobalFilterFn = (rows, ids, filterValue) => {
    if (rows && ids && filterValue) {
      return rows.filter((row) => {
        return ids.some((id) => {
          let rowValue = "";
          const field = dc.getField(id);
          let useIndexOf = false;
          if (field && field.type) {
            const type = field.type;

            if (type === "button" || !row.values[id]) {
              return false;
            }

            switch (type) {
              case "date":
              case "datetime":
                rowValue = row.values[id];
                return String(rowValue).toLowerCase().includes(String(filterValue).toLowerCase());
              case "currency":
                rowValue = row.values[id];
                break;
              case "active":
                rowValue = row.values[id];
                break;
              case "text":
                rowValue = row.values[id];
                useIndexOf = true;
                break;
              default:
                // rowValue = row.values[id] instanceof Object ? row.values[id].props ? row.values[id].props.value : row.values[id](0) : row.values[id];
                rowValue = row.values[id];
                break;
            }
          }
          if (useIndexOf) {
            return String(rowValue).toLowerCase().indexOf(String(filterValue).toLowerCase()) !== -1;
          }
          return String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase());
        });
      });
    }
    return [];
  };

  const initialSorts = useMemo(() => {
    try {
      if (userContext !== null) {
        const storedSorts = userContext.getRecent("sorts", identifier, []);
        if (Array.isArray(storedSorts) && storedSorts.every(item => typeof item.id === "string")) {
          return storedSorts as Array<SortingRule<ISort>>;
        }
        userContext.setRecent("sorts", identifier, []); // Reset to default if invalid
      }
    } catch (error) {
      console.error("Error fetching sorts from storage:", error);
    }
    return [] as Array<SortingRule<ISort>>;
  }, [src]);
  
  const initialFilters: Array<IFilterValue> = useMemo(() => {
    try {
      if (userContext !== null) {
        const storedFilters = userContext.getRecent("filters", identifier, []);
        if (Array.isArray(storedFilters) && storedFilters.every(item => typeof item.id === "string")) {
          return storedFilters as IFilterValue[];
        }
        userContext.setRecent("filters", identifier, []); // Reset to default if invalid
      }
    } catch (error) {
      console.error("Error fetching filters from storage:", error);
    }
    return [] as IFilterValue[];
  }, [src]);
  
  const savedInitialPageSize: number | null = useMemo(() => {
    try {
      if (userContext !== null) {
        const storedPageSize = userContext.getRecent("page_size", identifier, null);
        if (typeof storedPageSize === "number" && !isNaN(storedPageSize)) {
          return storedPageSize;
        }
        userContext.setRecent("page_size", identifier, 10); // Reset to default if invalid
      }
    } catch (error) {
      console.error("Error fetching page size from storage:", error);
    }
    return null;
  }, [src]);
  
  const initialPageIndex: number | null = useMemo(() => {
    try {
      if (userContext !== null) {
        const storedPageIndex = userContext.getRecent("page_index", identifier, null);
        if (typeof storedPageIndex === "number" && !isNaN(storedPageIndex)) {
          return storedPageIndex;
        }
        userContext.setRecent("page_index", identifier, 0); // Reset to default if invalid
      }
    } catch (error) {
      console.error("Error fetching page index from storage:", error);
    }
    return null;
  }, [src]);

  const userDefinedColumnVisibility = userContext !== null ?
    userContext.getRecent("columns", identifier, []) as Array<IColumn>
    : [];

  const userHiddenFieldNames = userDefinedColumnVisibility.filter(x => x.hidden === true).map(x => x.source);
  const userVisibleFieldNames = userDefinedColumnVisibility.filter(x => x.hidden === false).map(x => x.source);
  const uniqueHiddenFieldNames = Array.from(new Set(userHiddenFieldNames.concat(hiddenColumnNames)));

  const finalHiddenColumnNames = uniqueHiddenFieldNames.filter(x => userVisibleFieldNames.indexOf(x) === -1);

  const {
    getTableProps,
    toggleAllRowsSelected,
    headerGroups,
    rows,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    prepareRow,
    setAllFilters,
    setFilter,
    setHiddenColumns,
    state: { sortBy, pageIndex, pageSize, hiddenColumns, globalFilter, filters},
    selectedFlatRows
  } = useTable(
    {
      columns: columns,
      autoResetFilters: false,
      autoResetSortBy: false,
      autoResetPage: false,
      data: data,
      initialState: {
        pageSize: savedInitialPageSize || initialPageSize,
        hiddenColumns: finalHiddenColumnNames,
        sortBy: initialSorts,
        filters: initialFilters,
        pageIndex: initialPageIndex || 0
      },
      globalFilter: globalFilterFn
    },
    useFilters,
    useGlobalFilter, 
    useSortBy,
    usePagination,
    useRowSelect,
    (instance) => {
      instance.allColumns.push((columns) => {
        switch (allowSelection) {
          case "none":
          case "one":
            return [...columns];
          case "many":
            return [
              ...columns,
              {
                id: "selection",
                Header: ({ getToggleAllRowsSelectedProps }) => (
                  <div>
                    {allowSelection === "many" ? (
                      <TableCheckbox {...getToggleAllRowsSelectedProps()} title={"Toggle"} />
                    ) : null}
                  </div>
                ),
                Filter: () => (null),
                Cell: ({ row, toggleAllRowsSelected }) => {
                  return (
                    <div>
                      <TableCheckbox {...row.getToggleRowSelectedProps()} title={"Toggle"} />
                    </div>
                  );
                }
              }
            ];
          default:
            return [...columns];
        }
      });
    }
  );

  useEffect(() => {
    const rowElement = document.getElementById(`row-${rowId}`);
    if (rowElement) {
      rowElement.scrollIntoView({ behavior: 'smooth', block: "center" });
    }
  }, [pageIndex]);

  // jbg
  const skipPageResetRef = useRef(true);
  useEffect(() => {
    if (skipPageResetRef.current) {
      skipPageResetRef.current = false;
      return;
    }
    gotoPage(0);
  }, [globalFilter, filters, sortBy]);

  useEffect(()=> {
    if (userContext !== null) {
      userContext.setRecent('filters', identifier, filters);
    }
  },[filters])

  useEffect(()=> {
    if (userContext !== null) {
      userContext.setRecent('sorts', identifier, sortBy);
    }
  },[sortBy])

  useEffect(()=> {
    if (userContext !== null) {
      userContext.setRecent('page_index', identifier, pageIndex);
    }
  },[pageIndex])

  useEffect(()=> {
    if (userContext !== null) {
      userContext.setRecent('page_size', identifier, pageSize);
    }
  },[pageSize])

  const arrSelectedRowsIds = selectedFlatRows.map(x => x.id);

  // Try me and remove this boy one more time!
  console.log;

  if (!firstEntry && rows.length !== recordsNumber) {
    setRecordsNumber(rows.length);
    setFirstEntry(true);
  }

  const handleToggleFilter = function (evt: MouseEvent<HTMLButtonElement>) {
    if (showFilters) {
      setAllFilters([]);
    }

    if (userContext !== null) {
      userContext.setRecent("toggle_filter", identifier, {
        show: !showFilters,
      });
    }
    setShowFilters(prevState => !prevState);
  };


  // @Piero delete this
  const paginate = function () {
    if (rows.length >= pageSize /*&& pagination*/) {
      return page;
    } else {
      return rows;
    }
  };

  useEffect(() => {
    const actualSelectedRow = rows.find((x) => {
      const original = x.original as DCRecord;
      return rowId===original.id;
    }) 

    if (arrSelectedRowsIds.length>1 && allowSelection==="one") {   
      toggleAllRowsSelected(false);
      actualSelectedRow?.toggleRowSelected()
    }

  }, [rows]);

  const tableCellStyle = {
    padding: {
      xs: "2px 4px",
      md: "6px 16px"
    }
  };

  const textFilter = (isActive: boolean, lightTheme: boolean) => {
    if (!isActive) {
      if (lightTheme) {
        return "rgba(0,0,0,0.4)";
      } else {
        return "rgba(250,250,250,0.5)";
      }
    } else {
      return "";
    }
  };

  const handleToggleColumnVisibility = useCallback((source: any, isVisible: boolean) => {
    let _columns = userContext !== null ? userContext.getRecent("columns", identifier, []) as Array<IColumn> : [];
    const _item = _columns.find(x => x.source === source);

    let newHidden;
    if (isVisible) {
      newHidden = hiddenColumns ? hiddenColumns.filter(x => x !== source) : [];
      if (_item) {
        _item.hidden = false;
      } else {
        _columns.push({ source: source, hidden: false });
      }
    } else {
      newHidden = hiddenColumns ? hiddenColumns.concat([source]) : [source];
      if (!_item) {
        _columns.push({ source: source, hidden: true });
      } else {
        _item.hidden = true;
      }
    }

    const cleanArray = Array.from(new Set(newHidden));
    if (userContext !== null) {
      userContext.setRecent("columns", identifier, _columns);
    }
    setHiddenColumns(cleanArray);
    // NOTE: Does not work

    // setUserFieldVisibility(prevState => {
    //   return {
    //     ...prevState,
    //     [source]: isVisible
    //   }
    // })
  }, [hiddenColumns]);

  // useEffect(() => {
  //   setHiddenColumns(hiddenColumnNames);
  // }, [hiddenColumnNames])

  useEffect(() => {
    const selectedRecords = arrSelectedRowsIds.map((x) => records[x]);
    if (onSelectRecord) {
      onSelectRecord(selectedRecords);
    }
  }, [arrSelectedRowsIds]);

  const handleGlobalFilter = (value: string | undefined) => {
    setGlobalFilter(value);  
  }

  const clearSelection = () => { // Hacky!! But whole component also is. Table will not work well with allowSelection = many with existing code
    if (onRowClick)
      onRowClick(0);
    setRowId(-1)
    toggleAllRowsSelected(false)
  }

  useEffect(() => {
    if (!rowId) return;

    const foundRow = rows.find(row => {
      if ((row.original as DCRecord).id === rowId) {
        return true
      } else return false;
    })

    if (foundRow) {
      //goToSelectedRowPage(); Does not consistently work very well, so we are better off without his
    } else {
      clearSelection()
    }
    
  }, [globalFilter, filters])

  const paginationElement = records.length >= initialPageSize ? (
    <TablePagination
      component="div"
      sx={{ overflow: "unset", p: 0, width: isMdDown ? undefined : "fit-content" }}
      onPageChange={(evt, page) => {
        if (fetchData) {
          fetchData(pageSize, page).then(() => {
            gotoPage(page)
          })
        } else {
          gotoPage(page)
        }
      }}
      page={pageIndex}
      rowsPerPage={pageSize}
      count={rows.length}
      onRowsPerPageChange={(evt) => {
        const rowsPerPage = parseInt(evt.target.value, 10)
        if (fetchData) {
          fetchData(rowsPerPage, pageIndex).then(() => {
            setPageSize(rowsPerPage);
          })
        } else {
          if (records.length < rowsPerPage) {
            gotoPage(0);
          }
          setPageSize(rowsPerPage);
        }
      }}
    />
  ) : null;

  const footerChildrenElement = children ? (
    <Toolbar variant="dense" disableGutters={true} sx={isMdDown ? undefined : { flexGrow: 1 }}>
      <div style={{ flexGrow: 1 }} />
      {React.Children.map(children, (child) => {
        return child ? React.cloneElement(child, { selection: arrSelectedRowsIds }) : null;
      })
      .filter((x:any) => x !== undefined && x !== null)}
    </Toolbar>
  ) : null;

  // Render the UI for your table
  return (
    <Card
        sx={{
          height: "100%",
          p: 1
        }}
        elevation={0}
      // style={disableStyles ? { backgroundColor: "#eaeee2" } : {}}`
      >
      <CardContent  sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "100%",
        padding: `0px !important`
      }}>
      <Toolbar variant="regular" disableGutters={true}>
        {title ? <TableHeading>{title}</TableHeading> : null}
        {allowSearch ?
          <TableSearch
            // count={preGlobalFilteredRows ? preGlobalFilteredRows.length : 0}
            // value={globalFilter}
            // onChange={setGlobalFilter}
            dataCount={preGlobalFilteredRows.length}
            globalFilter={globalFilter}
            setGlobalFilter={handleGlobalFilter}
            disableSmallSearch={disableSmallSearch}
          />
          : null
        }
        {onRefresh ? <TableHeaderButtonRefresh onClick={() => onRefresh()} /> : null}
        {/*{*/}
        {/*  specificFilterField ? (*/}
        {/*      <TableSpecificFilters*/}
        {/*          setFilter={setFilter}*/}
        {/*          specificFilterField={specificFilterField}*/}
        {/*      />*/}
        {/*  ) : null*/}
        {/*}*/}
        <ToolbarFillContent />
        {allowColumnPicker ?
          <TableHeaderButtonColumnPicker columns={columns} hiddenColumnNames={hiddenColumns} forceIconButtons={forceIconButtons} onChange={handleToggleColumnVisibility} />
          : null}
        {allowFilter ?
          <TableHeaderButtonFilter filters={showFilters} forceIconButtons={forceIconButtons} onClick={handleToggleFilter} />
          : null}
        {allowExport ?
          <TableHeaderButtonExport dc={dc} rows={rows} forceIconButtons={forceIconButtons} records={records} tableName={tableName} viewName={viewName} exportLabel={exportLabel}/>
          : null}
        {allowAdd && handleAdd !== undefined ? (
          <TableHeaderButton onClick={() => handleAdd()} forceIconButtons={forceIconButtons} startIcon={<AddIcon />} variant="contained">
            {addLabel}
          </TableHeaderButton>
        ) : null}
      </Toolbar>
      <TableContainer sx={{
        // Selected indicator
        '& .MuiTableRow-root': {
          transition: (theme) => theme.transitions.create('box-shadow', {
            duration: theme.transitions.duration.short,
          }),
        },
        '& .MuiTableRow-root.Mui-selected': {
          boxShadow: (_) => `inset 3px 0 0 0 ${theme.palette.primary.main}`
        }
      }}>
        <MaterialUITable {...getTableProps()} size="small" stickyHeader={true}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}
              
              >
                {headerGroup.headers.map((column, c) => {
                  const { /*onClick,*/ ...headerProps } = column.getHeaderProps(column.getSortByToggleProps());
                  const id = column["id"];

                      return (
                        <TableCell
                          padding={column.id === "selection" ? "checkbox" : "normal"}
                          sx={column.id === "selection" ? undefined : tableCellStyle}
                          key={c}
                        >

                          {column.canSort ? (
                            <div {...headerProps}>
                              <TableSortLabel
                                active={column.isSorted}
                                direction={column.isSortedDesc ? "desc" : "asc"}
                              >
                                <Typography variant="caption" style={{ textTransform: "uppercase", fontWeight: 800 }}>{column.render("Header")}</Typography>
                              </TableSortLabel>
                            </div>
                          ) : (
                            <Typography variant="caption" style={{ textTransform: "uppercase", fontWeight: 800 }}>{column.render("Header")}</Typography>
                          )}
                          {
                            showFilters && column.filter ? (
                              <div className="table-filter" key={c + "-filter"}>
                                {column.render("Filter")}
                              </div>
                            ) : null}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {paginate().map((row: Row, i) => {
                  prepareRow(row);
                  const { original } = row as RowField;
                  // this comment fixes wrong selection on single select tables but what with multi select? -Piero
                  const isItemSelected = /*selectedRowIds[pageIndex * pageSize + i] === true ||*/ rowId === original['id'];
                  // this piece of work ensures that children of table get selection property. Is it useful here? Nobody knows
                  if (isItemSelected && arrSelectedRowsIds.length === 0) {
                    arrSelectedRowsIds.push(row.id);
                    row.toggleRowSelected();
                  }

                  // if this row has active as a property it will be a function which will return its boolean value before translation if true is passed to it
                  const isActive = !original.hasOwnProperty("active") || original['active'];
              return (
                <TableRow
                  hover={true}
                  selected={isItemSelected}
                  role="check"
                  aria-checked={isItemSelected}
                  {...row.getRowProps()}
                  onDoubleClick={(evt) => handleDoubleClick(evt, row.toggleRowSelected, toggleAllRowsSelected, original['id'])}
                  onClick={(evt) => handleRowClick(evt, row.toggleRowSelected, toggleAllRowsSelected, original['id'])}
                  id={`row-${original['id']}`}
                >
                  {row.cells.map((cell) => {
                    const field = dc.getField(cell.column.id);
                    const preserveNewlines = field ? field.type === "multiline" : false;

                    return <TableCell
                      sx={cell.column.id === "selection" ? undefined : tableCellStyle}
                      {...cell.getCellProps({
                        style: {
                          overflowWrap: "break-word",
                          whiteSpace: preserveNewlines ? "pre-line" : undefined,
                          cursor: "pointer",
                          color: textFilter(isActive, !!(themeContext !== null && themeContext.theme === "light"))
                        }
                      })}>{cell.render("Cell")}</TableCell>;
                  })}
                </TableRow>
              );
            })}
          </TableBody>
            </MaterialUITable>
          </TableContainer>
          {isMdDown ? (
            <>
              {paginationElement}
              {footerChildrenElement}
            </>
          ) : (
            paginationElement || footerChildrenElement ? (
              <Box sx={{ display: "flex" }}>
                {paginationElement}
                {footerChildrenElement}
              </Box>
            ) : null
          )}
        </CardContent>
      </Card>
  );
}

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  allowSelection: PropTypes.oneOf(["none", "one", "many"])
};

export default Table;
